import { Box, Row, Column, H2, GridContainer, BoundedContent, FlexBox, Link, Hidden } from '@vp/swan'
import AdaptiveCarouselTilesList from './AdaptiveCarouselTilesList'
import RowTilesList from './RowTilesList'
import { createOnClickTrackingHandler } from '../../utils/trackingUtils'
import { useTrackingContext } from '../../hooks/trackingContext'
import { getTrackingConfig, getTrackingConfigLinkAttributes } from '../../utils/trackingConfig'
import { useLogger, useUserContext } from '@vp/ubik-context'
import GridTilesList from './GridTilesList'
import type { TilesSectionProps } from '../../types/homepageFeatureTileRow'
import { FEATURE_TILE_SCHEMA } from '../../utils/trackingSchemas'
import { shouldShowPrice } from '../../utils/priceDisplay'

const getCarouselSlidesToShow = (visibleTiles: number) => ({
  xs: 2,
  sm: 2,
  md: visibleTiles,
  lg: visibleTiles,
  xl: visibleTiles,
})

const getRowSlidesToShow = (visibleTiles: number) => ({
  xs: 2,
  sm: 2,
  md: visibleTiles,
  lg: visibleTiles,
  xl: visibleTiles,
})

const TilesSection = (props: TilesSectionProps) => {
  const { tileData, tileImageLoadingStrategy, tileTrackingZone, id, tileRow, enableCampaignCallouts, enablePrice, gridToggle, carouselA11yText, locale } = props

  const { sectionHeader, tileList, tileSkin, callToAction, tileLayout } = tileData

  const logger = useLogger()

  if (!tileList?.length) {
    return <></>
  }

  const userContext = useUserContext()

  const customZone = tileRow ? `${tileTrackingZone}_${sectionHeader}_Row${tileRow}` : tileTrackingZone

  const { sourcePagePath } = useTrackingContext()

  // Narrow to the CTA object (rather than a boolean flag) so every reference below is provably
  // defined -- a separately-computed boolean can't carry that narrowing to later property reads.
  const definedCta = callToAction?.[0]?.targetUrl ? callToAction[0] : undefined
  const isCtaUrlDefined = !!definedCta
  const ctaUrl = definedCta ? definedCta.targetUrl.contentfulUrl : ''
  const ctaCopy = definedCta?.text
  const trackingData = {
    ctaCopy,
    sourcePagePath,
    ctaUrl,
    sectionHeader,
    row: Number(tileRow),
    section: tileTrackingZone,
  }

  const visibleTiles = tileLayout || 6

  const asCarousel = tileList.length > visibleTiles

  // Price is shown for the whole row or not at all — a row that mixes product and non-product tiles
  // would render prices on only some of its tiles.
  const showPrice = shouldShowPrice(enablePrice, tileList)

  if (userContext.experiments && userContext.experiments['homepage_cluster_tile_component'] === 'test' && ['1', '2', '4'].includes(tileRow)) {
    return null
  }

  const slidesToShowMap = asCarousel ? getCarouselSlidesToShow(visibleTiles) : getRowSlidesToShow(visibleTiles)

  const AdaptiveTiles = () => (
    <AdaptiveCarouselTilesList
      loadType={tileImageLoadingStrategy}
      enableCampaignCallouts={enableCampaignCallouts}
      showPrice={showPrice}
      tiles={tileList}
      trackingPageZone={customZone}
      id={id}
      tileSkin={tileSkin}
      visibleTiles={visibleTiles}
      slidesToShow={slidesToShowMap}
      carouselA11yText={carouselA11yText}
      sectionHeader={sectionHeader}
      locale={locale}
    />
  )

  const MobileCarouselTiles = AdaptiveTiles

  const RowTiles = () => (
    <RowTilesList
      loadType={tileImageLoadingStrategy}
      enableCampaignCallouts={enableCampaignCallouts}
      showPrice={showPrice}
      tiles={tileList}
      trackingPageZone={customZone}
      tileSkin={tileSkin}
      visibleTiles={visibleTiles}
      locale={locale}
    />
  )

  const DesktopTiles = asCarousel ? AdaptiveTiles : RowTiles

  return (
    <BoundedContent mb="between-sections">
      <GridContainer>
        <Box>
          {sectionHeader && (
            <Row>
              <Column
                span={12}
                onClick={isCtaUrlDefined ? createOnClickTrackingHandler(getTrackingConfig(trackingData), logger, FEATURE_TILE_SCHEMA, ctaUrl) : undefined}
                {...getTrackingConfigLinkAttributes(trackingData)}
              >
                <FlexBox alignItems="baseline" flexWrap="wrap" mb="6">
                  {sectionHeader.trim().length > 0 && (
                    <H2 margin={0} fontSkin="title-section" mb="0">
                      {sectionHeader}
                    </H2>
                  )}
                  {definedCta && (
                    <Link ml="5" href={definedCta.targetUrl.contentfulUrl} skin="cta" style={{ whiteSpace: 'nowrap' }}>
                      {definedCta.text}
                    </Link>
                  )}
                </FlexBox>
              </Column>
            </Row>
          )}
          <Hidden md lg xl>
            {gridToggle ? (
              <GridTilesList
                loadType={tileImageLoadingStrategy}
                enableCampaignCallouts={enableCampaignCallouts}
                showPrice={showPrice}
                tiles={tileList}
                trackingPageZone={customZone}
                id={id}
                tileSkin={tileSkin}
                locale={locale}
              />
            ) : (
              <MobileCarouselTiles />
            )}
          </Hidden>
          <Hidden xs sm>
            <DesktopTiles />
          </Hidden>
        </Box>
      </GridContainer>
    </BoundedContent>
  )
}

export default TilesSection
